import React from "react";
import {
  ColumnWrapper,
  SectionGroupLink,
  SectionTitle,
  SubmenuGrid,
} from "./components";
interface Item {
  name: string;
  link: string;
  count?: number | null;
}

interface Split {
  name: string;
  items: Item[];
}

type FlowItem = { title?: string; item: Item; category?: string };

export const renderSevenPlusCategories = (
  splits: Split[],
  departmentName?: string,
) => {
  if (!splits || splits.length === 0) return null;

  const maxColumns = 6;

  // Flatten categories
  const flattened: FlowItem[] = [];
  splits.forEach((split) => {
    split.items.forEach((item, i) => {
      flattened.push({
        title: i === 0 ? split.name : undefined,
        category: split.name,
        item,
      });
    });
  });

  const totalItems = flattened.length;
  const targetPerColumn = Math.ceil(totalItems / maxColumns);

  const columns: FlowItem[][] = Array.from({ length: maxColumns }, () => []);
  let colIndex = 0;
  let countInCol = 0;

  for (let i = 0; i < flattened.length; i++) {
    const current = flattened[i];
    columns[colIndex].push(current);
    countInCol++;

    const next = flattened[i + 1];
    if (!next) continue;

    let remainingInCategory = 0;
    for (let j = i + 1; j < flattened.length; j++) {
      if (flattened[j].category === current.category) remainingInCategory++;
      else break;
    }

    const nextIsLastInCategory = remainingInCategory === 1;
    const reachedColumnTarget = countInCol >= targetPerColumn;

    if (
      reachedColumnTarget &&
      colIndex < maxColumns - 1 &&
      !nextIsLastInCategory
    ) {
      colIndex++;
      countInCol = 0;
    }
  }

  return (
    <SubmenuGrid departmentName={departmentName} columns={maxColumns}>
      {columns.map((column, colIdx) => (
        <ColumnWrapper key={`column-${colIdx}`}>
          {column.map((flowItem, idx) => (
            <React.Fragment key={`item-${colIdx}-${idx}`}>
              {flowItem.title && <SectionTitle>{flowItem.title}</SectionTitle>}
              <SectionGroupLink href={flowItem.item.link}>
                {flowItem.item.name}
              </SectionGroupLink>
            </React.Fragment>
          ))}
        </ColumnWrapper>
      ))}
    </SubmenuGrid>
  );
};

export const renderSingleCategory = (split: Split, departmentName?: string) => {
  const maxLinksPerCol = 6;
  const totalItems = split.items.length;
  const totalColumns = Math.ceil(totalItems / maxLinksPerCol);

  // Initialize empty columns
  const columns: Item[][] = Array.from({ length: totalColumns }, () => []);

  // Distribute items top-to-bottom, then left-to-right
  split.items.forEach((item, idx) => {
    const colIdx = Math.floor(idx / maxLinksPerCol);
    columns[colIdx].push(item);
  });

  return (
    <>
      <SectionTitle>{split.name}</SectionTitle>

      <SubmenuGrid columns={totalColumns}>
        {columns.map((columnItems, colIdx) => (
          <ColumnWrapper key={colIdx}>
            {columnItems.map((item, i) => (
              <SectionGroupLink key={i} href={item.link}>
                {item.name}
              </SectionGroupLink>
            ))}
          </ColumnWrapper>
        ))}
      </SubmenuGrid>
    </>
  );
};

export const renderFewCategories = (
  splits: Split[],
  departmentName?: string,
) => {
  const maxColumns = 6;

  return (
    <SubmenuGrid departmentName={departmentName} columns={maxColumns}>
      {splits.map((split, idx) => (
        <ColumnWrapper key={`category-${split.name}-${idx}`}>
          <SectionTitle>{split.name}</SectionTitle>
          {split.items.map((item, i) => (
            <React.Fragment key={i}>
              <SectionGroupLink href={item.link}>{item.name}</SectionGroupLink>
            </React.Fragment>
          ))}
        </ColumnWrapper>
      ))}
    </SubmenuGrid>
  );
};
